import React, { useEffect, useMemo, useRef, useState } from 'react';
import { predictWithWavBlob, convertToWav, doctorStartSession, doctorSaveEmotions, doctorAddNote } from './api';
import { createPeerConnection, createWebSocketConnection } from '../utils/webrtcConfig';

export default function VideoCallModal({ doctorId, patient, onClose, onSessionSaved }) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const remoteStreamRef = useRef(null);

  const [recordingIdx, setRecordingIdx] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [, setSessionId] = useState(null);
  const [segments, setSegments] = useState([]); // mapped emotion results per segment
  const [assessmentChoice, setAssessmentChoice] = useState('');
  const [sessionNote, setSessionNote] = useState('');
  const [saved, setSaved] = useState(false);

  const roomId = useMemo(() => {
    const preset = (typeof window !== 'undefined' && window.__VIDEO_ROOM_ID__) || null;
    if (preset) return preset;
    const ts = Date.now();
    return `doc_${doctorId}_pat_${patient?.id}_${ts}`;
  }, [doctorId, patient]);

  const joinUrl = useMemo(() => {
    const base = `${window.location.protocol}//${window.location.host}`;
    return `${base}/join?room=${encodeURIComponent(roomId)}`;
  }, [roomId]);

  // Broadcast an invite so the patient tab (Home.jsx) can show a Join banner
  useEffect(() => {
    try {
      const ibc = new BroadcastChannel('rtc_invites');
      // Include outpatientUserId so only the intended patient shows the banner
      const targetUid = (typeof window !== 'undefined' && window.__VIDEO_OUTPATIENT_UID__) || null;
      ibc.postMessage(JSON.stringify({ roomId, joinUrl, outpatientUserId: targetUid }));
      // Close after broadcasting to avoid leaks; Home.jsx will own the channel
      setTimeout(() => { try { ibc.close(); } catch {} }, 200);
    } catch {}
  }, [roomId, joinUrl]);

  useEffect(() => {
    (async () => {
      try {
        // Setup peer connection with comprehensive STUN/TURN servers for global connectivity
        const pc = createPeerConnection();
        pcRef.current = pc;

        // Local media (doctor camera/mic)
        const localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
        if (localVideoRef.current) localVideoRef.current.srcObject = localStream;

        // Remote stream
        const remoteStream = new MediaStream();
        remoteStreamRef.current = remoteStream;
        pc.ontrack = (ev) => {
          ev.streams[0].getTracks().forEach(track => remoteStream.addTrack(track));
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
        };

        // WebSocket signaling (supports both dev and live deployment)
        let ws;
        try {
          ws = await createWebSocketConnection(`room/${roomId}`);
        } catch (e) {
          // Fallback for local testing: BroadcastChannel on same origin tabs
          try {
            const bc = new BroadcastChannel(`rtc_${roomId}`);
            wsRef.current = {
              readyState: 1,
              send: (payload) => bc.postMessage(payload),
              close: () => { try { bc.close(); } catch {} },
              addEventListener: () => {},
            };
            bc.onmessage = async (ev) => {
              try {
                const msg = JSON.parse(ev.data || ev); // handle string/object
                if (msg.sdp) {
                  await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
                  if (msg.sdp.type === 'offer') {
                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    bc.postMessage(JSON.stringify({ sdp: pc.localDescription }));
                  }
                } else if (msg.candidate) {
                  try { await pc.addIceCandidate(msg.candidate); } catch {}
                }
              } catch {}
            };
            setMessage('Using local signaling channel');
          } catch (e) {
            setMessage('Unable to open signaling channel (WS).');
            return;
          }
        }
        
        if (ws) {
          wsRef.current = ws;
        }
        const pendingCandidates = [];

        const onSignal = async (raw) => {
          const msg = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
          if (msg.type === 'hello') {
            // New peer joined → resend our current offer to kick off handshake
            try {
              if (pc.localDescription) {
                wsRef.current && wsRef.current.send(JSON.stringify({ sdp: pc.localDescription }));
              } else {
                const offerNow = await pc.createOffer();
                await pc.setLocalDescription(offerNow);
                wsRef.current && wsRef.current.send(JSON.stringify({ sdp: pc.localDescription }));
              }
            } catch {}
            return;
          }
          if (msg.sdp) {
            await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
            if (msg.sdp.type === 'offer') {
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              try { wsRef.current.send(JSON.stringify({ sdp: pc.localDescription })); } catch {}
            }
          } else if (msg.candidate) {
            try { await pc.addIceCandidate(msg.candidate); } catch {}
          }
        };
        if (ws && ws.onmessage !== undefined) {
          ws.onmessage = (ev) => onSignal(ev.data);
          // Announce presence so caller can resend offer if needed
          try { ws.send(JSON.stringify({ type: 'hello' })); } catch {}
        }

        pc.onicecandidate = (ev) => {
          if (ev.candidate) {
            const payload = JSON.stringify({ candidate: ev.candidate });
            if (wsRef.current && wsRef.current.readyState === 1) {
              try { wsRef.current.send(payload); } catch {}
            } else {
              pendingCandidates.push(payload);
            }
          }
        };

        // Create and send offer immediately (doctor initiates)
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        if (ws && ws.readyState === 1) {
          // Socket already open (we awaited open earlier) → send now
          try { ws.send(JSON.stringify({ sdp: pc.localDescription })); } catch {}
          while (pendingCandidates.length && ws.readyState === 1) {
            try { ws.send(pendingCandidates.shift()); } catch { break; }
          }
        } else if (ws && ws.addEventListener) {
          // Edge case: if not yet open, send once it opens
          ws.addEventListener('open', () => {
            try { ws.send(JSON.stringify({ sdp: pc.localDescription })); } catch {}
            while (pendingCandidates.length && ws.readyState === 1) {
              try { ws.send(pendingCandidates.shift()); } catch { break; }
            }
            try { ws.send(JSON.stringify({ type: 'hello' })); } catch {}
          });
        } else {
          // BroadcastChannel path: send our offer immediately
          try { wsRef.current.send(JSON.stringify({ sdp: pc.localDescription })); } catch {}
        }
      } catch (e) {
        setMessage('Failed to initialize video call.');
      }
    })();

    return () => {
      try { wsRef.current && wsRef.current.close(); } catch {}
      try { pcRef.current && pcRef.current.close(); } catch {}
    };
  }, [roomId]);

  function parseToUiEmotions(apiRes) {
    // Expect shape: { RES: { top_5: 'happy 30.00%, sad 10.00%, ...' } }
    const res = apiRes?.RES || apiRes?.res || apiRes || {};
    const top = String(res.top_5 || '');
    const getNum = (k) => {
      const m = top.match(new RegExp(`${k}\\s+(\\d+(?:\\.\\d+)?)%`, 'i'));
      return m ? parseFloat(m[1]) : 0;
    };
    const happy = getNum('happy');
    const sad = getNum('sad');
    const angry = getNum('angry');
    const fear = getNum('fear');
    const disgust = getNum('disgust');
    const joy = happy;
    const stress = fear;
    const sadness = sad;
    const anger = angry;
    const hurt = disgust;
    const burnout_score = Math.max(0, Math.min(100, Math.round((stress + sadness + anger + hurt) / 4)));
    return { joy, stress, sadness, anger, hurt, burnout_score };
  }

  async function recordOnce() {
    if (isRecording) return;
    const remoteStream = remoteStreamRef.current;
    if (!remoteStream) { setMessage('Waiting for patient to join...'); return; }

    setIsRecording(true);
    setMessage(`Recording ${recordingIdx + 1}/5 ...`);
    // Build an audio-only stream to avoid MediaRecorder failures on some browsers
    const audioTracks = remoteStream.getAudioTracks();
    if (!audioTracks || audioTracks.length === 0) {
      setIsRecording(false);
      setMessage('No remote audio track. Ask patient to enable microphone.');
      return;
    }
    const audioOnlyStream = new MediaStream();
    audioTracks.forEach(t => audioOnlyStream.addTrack(t));

    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : undefined;
    let mr;
    let stopTimer;
    try {
      mr = new MediaRecorder(audioOnlyStream, mime ? { mimeType: mime } : undefined);
    } catch (e) {
      setIsRecording(false);
      setMessage('Audio recorder not supported in this browser.');
      return;
    }
    const chunks = [];
    mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunks.push(ev.data); };
    mr.onstop = async () => {
      try {
        const blob = new Blob(chunks, { type: mime || 'audio/webm' });
        // Convert to wav via backend (robust) then predict
        const convRes = await convertToWav(blob);
        const wavBase64 = convRes?.data?.wav_base64;
        if (!wavBase64) throw new Error('convert: empty wav');
        const wavBytes = atob(wavBase64);
        const buf = new Uint8Array(wavBytes.length);
        for (let i = 0; i < wavBytes.length; i++) buf[i] = wavBytes.charCodeAt(i);
        const wavBlob = new Blob([buf], { type: 'audio/wav' });
        const predRes = await predictWithWavBlob(wavBlob);
        const apiRes = predRes?.data;
        const mapped = parseToUiEmotions(apiRes?.RES || apiRes);
        setSegments(prev => [...prev, mapped]);
        setRecordingIdx(prev => prev + 1);
        setMessage(`Recorded ${recordingIdx + 1}/5`);
      } catch (e) {
        setMessage('Prediction failed for this clip.');
      } finally {
        try { clearTimeout(stopTimer); } catch {}
        setIsRecording(false);
      }
    };
    try {
      mr.start();
      stopTimer = setTimeout(() => { try { mr.stop(); } catch {} }, 8000);
    } catch (e) {
      setIsRecording(false);
      setMessage('Failed to start audio recorder.');
      try { mr && mr.stop && mr.stop(); } catch {}
    }
  }

  function averageSegments(items) {
    const n = items.length || 1;
    const sum = items.reduce((a, b) => ({
      joy: a.joy + (b.joy || 0),
      stress: a.stress + (b.stress || 0),
      sadness: a.sadness + (b.sadness || 0),
      anger: a.anger + (b.anger || 0),
      hurt: a.hurt + (b.hurt || 0),
      burnout_score: a.burnout_score + (b.burnout_score || 0)
    }), { joy:0, stress:0, sadness:0, anger:0, hurt:0, burnout_score:0 });
    return {
      joy: Math.round(sum.joy / n),
      stress: Math.round(sum.stress / n),
      sadness: Math.round(sum.sadness / n),
      anger: Math.round(sum.anger / n),
      hurt: Math.round(sum.hurt / n),
      burnout_score: Math.round(sum.burnout_score / n)
    };
  }

  async function saveSession() {
    if (segments.length < 5 || !patient) { setMessage('Complete all 5 recordings first.'); return; }
    if (!assessmentChoice) { setMessage('Select a clinical assessment before saving.'); return; }
    setBusy(true);
    try {
      // Start a doctor session
      const startRes = await doctorStartSession(patient.id, null);
      const sid = startRes?.data?.session_id || startRes?.data?.sessionId || startRes?.session_id;
      setSessionId(sid);
      const avg = averageSegments(segments);
      const payload = {
        doctor_patient_id: patient.id,
        session_id: sid,
        joy: avg.joy,
        stress: avg.stress,
        sadness: avg.sadness,
        anger: avg.anger,
        burnout_score: avg.burnout_score,
        doctor_assessment: assessmentChoice
      };
      await doctorSaveEmotions(payload);
      if (sessionNote.trim()) {
        try {
          await doctorAddNote(sid, sessionNote.trim());
        } catch (e) {
          console.error('Failed to save session note:', e);
          setMessage('Session saved but note failed to save.');
        }
      }
      setSaved(true);
      setMessage('Session completed, and report saved.');
      if (onSessionSaved) onSessionSaved();
    } catch (e) {
      setMessage('Failed to save session.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={() => onClose && onClose()}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Video Call - {patient?.name || 'Patient'}</h2>
          <button className="close-btn" onClick={() => onClose && onClose()}>×</button>
        </div>
        <div className="modal-body">
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px'
          }}>
            <div style={{ fontSize: 12, opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Invite link: {joinUrl}
            </div>
            <button className="report-btn" onClick={() => {
              navigator.clipboard.writeText(joinUrl)
                .then(() => setMessage('Invite link copied'))
                .catch(() => setMessage('Failed to copy link'));
            }}>Copy</button>
            <button className="report-btn" onClick={() => {
              try { window.open(joinUrl, '_blank', 'noopener'); } catch {}
            }}>Open</button>
            <button className="report-btn" onClick={async () => {
              try {
                // 1) Local broadcast (same-profile/tab fallback)
                const ibc = new BroadcastChannel('rtc_invites');
                const targetUid = (typeof window !== 'undefined' && window.__VIDEO_OUTPATIENT_UID__) || null;
                ibc.postMessage(JSON.stringify({ roomId, joinUrl, outpatientUserId: targetUid }));
                setTimeout(() => { try { ibc.close(); } catch {} }, 200);
                try {
                  const payload = { roomId, joinUrl, outpatientUserId: targetUid, ts: Date.now() };
                  localStorage.setItem('rtc_invite', JSON.stringify(payload));
                } catch {}

                // 2) Backend push over per-user WS channel (cross-profile delivery)
                if (targetUid) {
                  const base = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
                    ? process.env.REACT_APP_API_URL
                    : (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
                        ? 'http://localhost:8000'
                        : `${window.location.protocol}//${window.location.host}/api`);
                  try {
                    await fetch(`${base}/notify/invite`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ to: String(targetUid), joinUrl, roomId, fromUser: String(doctorId || '') })
                    });
                  } catch {}
                }

                setMessage('Invite sent');
              } catch {}
            }}>Send Invite</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <video ref={localVideoRef} autoPlay muted playsInline style={{ width: '100%', borderRadius: 8, background: '#000' }} />
              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>You</div>
            </div>
            <div>
              <video ref={remoteVideoRef} autoPlay playsInline style={{ width: '100%', borderRadius: 8, background: '#000' }} />
              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>Patient</div>
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Clinical Assessment</div>
              <select
                value={assessmentChoice}
                onChange={(e) => setAssessmentChoice(e.target.value)}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', outline: 'none', background: '#fff' }}
              >
                <option value="">Select assessment…</option>
                <option value="Stable">Stable</option>
                <option value="Moderate Risk">Moderate Risk</option>
                <option value="High Risk">High Risk</option>
              </select>
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Session Note</div>
              <textarea
                value={sessionNote}
                onChange={(e) => setSessionNote(e.target.value)}
                rows={3}
                placeholder="Add session note (optional)"
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', outline: 'none' }}
              />
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="report-btn" disabled={isRecording || recordingIdx >= 5} onClick={recordOnce}>
              {isRecording ? 'Recording...' : `Record (${recordingIdx}/5)`}
            </button>
            <button className="report-btn" style={{ backgroundColor: '#2563eb', color: '#fff' }} disabled={segments.length < 5 || busy} onClick={saveSession}>
              Save Session
            </button>
            <button
              className="report-btn"
              style={{ marginLeft: 'auto', backgroundColor: '#ef4444', color: '#fff' }}
              disabled={!saved}
              onClick={() => {
                try { wsRef.current && wsRef.current.send(JSON.stringify({ type: 'hangup' })); } catch {}
                try { wsRef.current && wsRef.current.close && wsRef.current.close(); } catch {}
                try { pcRef.current && pcRef.current.close && pcRef.current.close(); } catch {}
                if (onClose) onClose();
              }}
            >
              Hang Up
            </button>
            <div style={{ fontWeight: 600 }}>{segments.length}/5 clips</div>
          </div>

          {message && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#eef2ff', borderRadius: 8 }}>{message}</div>
          )}
        </div>
      </div>
    </div>
  );
}


